import type { TppCardSummary } from '../../../services/transitPassPool.service';
import type { TppProofDisplayKind } from '../../../services/systems/transit-pass-pool/tppProofFile';
import TppOpsModals from './TppOpsModals';
import TppCardMasterModals from './TppCardMasterModals';
import TppDataModals from './TppDataModals';
import type { TppPoolSettingsSeed } from './tppDataModalTypes';
import TppEventEditModal from './TppEventEditModal';
import TppProofViewerModal from './TppProofViewerModal';
import { nextTppDisplayNumberForPool } from './tppUiShared';
import type { useTppPageMutations } from './useTppPageMutations';

type Mutations = ReturnType<typeof useTppPageMutations>;

type Props = {
  closeLabel: string;
  money: (n: number) => string;
  zeroBalanceLabel: string;
  poolKey: string;
  selected: TppCardSummary | null;
  otherCheckedOutCount: number;
  poolSettingsSeed: TppPoolSettingsSeed | undefined;
  mut: Mutations;
  proof: {
    open: boolean;
    loading: boolean;
    error: string | null;
    fileUrl: string | null;
    displayKind: TppProofDisplayKind | null;
    fileName?: string | null;
    onHide: () => void;
  };
};

/** Zip 接続後の全モーダル配線（ページ本体を薄く保つ） */
export default function TppPageModals({
  closeLabel,
  money,
  zeroBalanceLabel,
  poolKey,
  selected,
  otherCheckedOutCount,
  poolSettingsSeed,
  mut,
  proof,
}: Props) {
  const { modals } = mut;

  return (
    <>
      <TppDataModals
        closeLabel={closeLabel}
        poolKey={poolKey}
        poolSettingsSeed={poolSettingsSeed}
        exportOpen={modals.exportModalOpen}
        onExportHide={modals.hideExport}
        exportPending={mut.exportMut.isPending}
        exportError={mut.exportMut.isError ? mut.exportMut.error : null}
        onExportSubmit={(payload) => mut.exportMut.mutate(payload)}
        importOpen={modals.importExcelModalOpen}
        onImportHide={modals.hideImportExcel}
        importPending={mut.importExcelMut.isPending}
        importError={mut.importExcelMut.isError ? mut.importExcelMut.error : null}
        onImportSubmit={(file) => mut.importExcelMut.mutate(file)}
        onImportErrorReset={mut.importExcelMut.reset}
        poolSettingsOpen={modals.poolSettingsModalOpen}
        onPoolSettingsHide={modals.hidePoolSettings}
        poolSettingsPending={mut.poolSettingsMut.isPending}
        poolSettingsError={mut.poolSettingsMut.isError ? mut.poolSettingsMut.error : null}
        onPoolSettingsSubmit={(payload) => mut.poolSettingsMut.mutate(payload)}
      />

      <TppEventEditModal
        closeLabel={closeLabel}
        open={modals.eventEditOpen}
        onHide={modals.hideEventEdit}
        seed={modals.eventEditSeed}
        pending={mut.patchEventMut.isPending}
        error={mut.patchEventMut.isError ? mut.patchEventMut.error : null}
        onSubmit={(payload) => mut.patchEventMut.mutate(payload)}
      />

      <TppProofViewerModal
        closeLabel={closeLabel}
        open={proof.open}
        onHide={proof.onHide}
        loading={proof.loading}
        error={proof.error}
        fileUrl={proof.fileUrl}
        displayKind={proof.displayKind}
        fileName={proof.fileName}
      />

      <TppOpsModals
        closeLabel={closeLabel}
        money={money}
        zeroBalanceLabel={zeroBalanceLabel}
        balanceNumber={selected?.balanceNumber ?? null}
        isCheckedOut={!!selected?.isCheckedOut}
        otherCheckedOutCount={otherCheckedOutCount}
        returnRouteSeed={modals.returnRouteSeed}
        borrowOpen={modals.checkoutOpen}
        onBorrowHide={modals.hideCheckout}
        borrowPending={mut.checkoutMut.isPending}
        onBorrowSubmit={(payload) => mut.checkoutMut.mutate(payload)}
        returnOpen={modals.checkinOpen}
        onReturnHide={modals.hideCheckin}
        returnPending={mut.checkinMut.isPending}
        onReturnSubmit={(payload) => mut.checkinMut.mutate(payload)}
        chargeOpen={modals.chargeOpen}
        onChargeHide={modals.hideCharge}
        chargePending={mut.chargeMut.isPending}
        onChargeSubmit={(payload) => mut.chargeMut.mutate(payload)}
      />

      <TppCardMasterModals
        closeLabel={closeLabel}
        createOpen={modals.createCardOpen}
        onCreateHide={modals.hideCreateCard}
        createNextDisplayNumber={nextTppDisplayNumberForPool(poolKey)}
        createPending={mut.createCardMut.isPending}
        createError={mut.createCardMut.isError ? mut.createCardMut.error : null}
        onCreateSubmit={(payload) => mut.createCardMut.mutate(payload)}
        editOpen={modals.editCardOpen}
        onEditHide={modals.hideEditCard}
        editSeed={
          selected
            ? {
                displayNumber: selected.displayNumber,
                poolKey: selected.poolKey,
                physicalId: selected.physicalId,
                isActive: selected.isActive,
              }
            : null
        }
        editPending={mut.patchCardMut.isPending}
        editError={mut.patchCardMut.isError ? mut.patchCardMut.error : null}
        onEditSubmit={(payload) => mut.patchCardMut.mutate(payload)}
      />
    </>
  );
}
